import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PARTIAL_LIMIT_DEFAULTS } from "@logiful/domain";
import { PackageX } from "lucide-react";
import { toast } from "sonner";
import { useTenant } from "@/components/AppShell";
import { Card, cx, Input } from "@/components/ui";
import { api, canManage, type BackorderSettings } from "@/lib/api";
import { t, msg } from "@/lib/i18n";

type Choice = "inherit" | "hold" | "partial" | "transfer-fulfiller" | "transfer-merchant";

const OPTIONS: { value: Exclude<Choice, "inherit">; title: string; text: string }[] = [
  { value: "hold", title: msg("Im Portal zurückhalten"), text: msg("Auftrag wartet mit Grund „Fehlbestand“ und geht automatisch raus, sobald ihm nach der Bestandsvergabe Bestand zugeteilt ist.") },
  { value: "partial", title: msg("Teilliefern"), text: msg("Lieferbare Positionen gehen sofort raus. Der Rest wird eine verknüpfte Restlieferung (z. B. #1002-T2), die automatisch folgt, sobald Bestand da ist.") },
  { value: "transfer-fulfiller", title: msg("Trotzdem direkt an Logiful"), text: msg("Logiful sieht den Auftrag sofort und versendet, sobald die Ware eingebucht ist. Achtung: Welcher Auftrag die eingehende Ware bekommt, entscheidet dann das Lager – die Bestandsvergabe gilt nicht.") },
  { value: "transfer-merchant", title: msg("Nur ins FFN, an Logiful sobald Bestand da ist"), text: msg("Auftrag liegt im FFN in „Vorbereitung“. Das Portal übergibt ihn an Logiful, sobald der Bestand reicht.") },
];

const toChoice = (s: Partial<BackorderSettings>): Choice => (!s.mode ? "inherit" : s.mode === "hold" ? "hold" : s.mode === "partial" ? "partial" : s.handover === "merchant" ? "transfer-merchant" : "transfer-fulfiller");
const fromChoice = (c: Choice): Partial<BackorderSettings> =>
  c === "inherit" ? {} : c === "hold" ? { mode: "hold", handover: "fulfiller" } : c === "partial" ? { mode: "partial", handover: "fulfiller", ...PARTIAL_LIMIT_DEFAULTS } : { mode: "transfer", handover: c === "transfer-merchant" ? "merchant" : "fulfiller" };
const label = (c: Choice) => OPTIONS.find((o) => o.value === c)?.title ?? "";

type Limits = { minSharePercent: number | null; maxParts: number | null };

/** Grenzen der Teillieferung – leer = keine Grenze (auch in der Regel-Aktion „Bei Fehlbestand“) */
export function PartialLimitsEditor({ value, onChange, disabled }: { value: { minSharePercent?: number | null; maxParts?: number | null }; onChange: (v: Limits) => void; disabled?: boolean }) {
  const cur: Limits = { minSharePercent: value.minSharePercent ?? null, maxParts: value.maxParts ?? null };
  const num = (raw: string, min: number, max: number) => { const n = Math.round(Number(raw)); return raw.trim() && n ? Math.min(max, Math.max(min, n)) : null; };
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[12.5px] text-fg-muted">
      <span>{t("Teilliefern ab")}</span>
      <Input type="number" min={1} max={99} disabled={disabled} defaultValue={cur.minSharePercent ?? ""} key={`s${cur.minSharePercent}`} placeholder="–" suffix="%" className="h-7 w-20 tabular-nums" aria-label={t("Mindestanteil")} onBlur={(e) => { const v = num(e.target.value, 1, 99); if (v !== cur.minSharePercent) onChange({ ...cur, minSharePercent: v }); }} />
      <span>{t("lieferbar, höchstens")}</span>
      <Input type="number" min={2} max={20} disabled={disabled} defaultValue={cur.maxParts ?? ""} key={`p${cur.maxParts}`} placeholder="∞" className="h-7 w-16 tabular-nums" aria-label={t("Höchstzahl Teile")} onBlur={(e) => { const v = num(e.target.value, 2, 20); if (v !== cur.maxParts) onChange({ ...cur, maxParts: v }); }} />
      <span>{t("Teile je Auftrag")}</span>
      <span className="basis-full text-[11.5px] text-fg-subtle">{t("Anteil nach Warenwert (ohne Preise nach Stückzahl). Ist weniger lieferbar oder sind schon genug Teile unterwegs, wartet der Auftrag, bis alles da ist. Leer = keine Grenze.")}</span>
    </div>
  );
}

function ChoiceList({ value, onChange, disabled, inheritText, limits, onLimits }: { value: Choice; onChange: (c: Choice) => void; disabled: boolean; inheritText?: string; limits?: Partial<BackorderSettings>; onLimits?: (l: Limits) => void }) {
  const options = inheritText ? [{ value: "inherit" as const, title: msg("Wie beim Kunden eingestellt"), text: inheritText }, ...OPTIONS] : OPTIONS;
  return (
    <div className="mt-3 grid gap-2">
      {options.map((o) => (
        <label key={o.value} className={cx("flex items-start gap-2.5 rounded-xl border p-3 transition-colors", value === o.value ? "border-ink bg-surface-2" : "border-border hover:bg-surface-2/60", disabled ? "cursor-default" : "cursor-pointer")}>
          <input type="radio" className="mt-1 accent-ink" checked={value === o.value} disabled={disabled} onChange={() => onChange(o.value)} />
          <span>
            <span className="block text-[13px] font-medium">{t(o.title)}</span>
            <span className="block text-[12px] text-fg-muted">{t(o.text)}</span>
            {o.value === "partial" && value === "partial" && limits && onLimits && (
              <span className="block mt-2.5" onClick={(e) => e.preventDefault()}><PartialLimitsEditor value={limits} onChange={onLimits} disabled={disabled} /></span>
            )}
          </span>
        </label>
      ))}
    </div>
  );
}

/** Kunden-Ebene: gilt für alle Shops, solange ein Shop nichts anderes festlegt */
export function BackorderSettingsCard() {
  const { tenantId, role } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["backorder", tenantId], queryFn: () => api.orders.backorderSettings(tenantId) });
  const save = useMutation({
    mutationFn: (s: BackorderSettings) => api.orders.saveBackorderSettings(tenantId, s),
    onSuccess: () => {
      toast.success("Gespeichert", { description: msg("Zurückgehaltene Aufträge werden gleich neu geprüft.") });
      void qc.invalidateQueries({ queryKey: ["backorder", tenantId] });
    },
    onError: () => toast.error("Speichern fehlgeschlagen"),
  });
  if (!q.data) return null;
  const current = save.isPending && save.variables ? save.variables : q.data;
  return (
    <Card className="p-5 mb-4">
      <h2 className="font-semibold text-[14px] flex items-center gap-2"><PackageX className="size-4 text-fg-muted" /> {t("Aufträge mit Fehlbestand")}</h2>
      <p className="text-[12.5px] text-fg-muted mt-0.5">{t("Gilt für alle Shops – je Shop abweichend einstellbar. Am einzelnen Auftrag lässt sich die Übertragung mit „Trotz Fehlbestand übertragen“ jederzeit bis zu Logiful erzwingen.")}</p>
      <ChoiceList value={toChoice(current)} disabled={!canManage(role)} onChange={(c) => save.mutate(fromChoice(c) as BackorderSettings)} limits={current} onLimits={(l) => save.mutate({ ...current, ...l })} />
      <p className="mt-2 text-[12px] text-fg-subtle">{t("Abweichend je Auftrag: Regel-Aktion „Bei Fehlbestand“ in den Auftragsregeln (z. B. nur Inland teilliefern).")}</p>
    </Card>
  );
}

/** Shop-Ebene: überschreibt den Kunden oder übernimmt ihn */
export function ChannelBackorderCard({ channelId, override }: { channelId: string; override: Partial<BackorderSettings> }) {
  const { tenantId, role } = useTenant();
  const qc = useQueryClient();
  const tenant = useQuery({ queryKey: ["backorder", tenantId], queryFn: () => api.orders.backorderSettings(tenantId) });
  const save = useMutation({
    mutationFn: (o: Partial<BackorderSettings>) => api.channels.setBackorder(tenantId, channelId, o),
    onSuccess: () => {
      toast.success("Gespeichert", { description: msg("Zurückgehaltene Aufträge werden gleich neu geprüft.") });
      void qc.invalidateQueries({ queryKey: ["channel", tenantId, channelId] });
    },
    onError: () => toast.error("Speichern fehlgeschlagen"),
  });
  const current = save.isPending && save.variables ? save.variables : override;
  return (
    <Card className="p-5">
      <h2 className="font-semibold text-[14px] flex items-center gap-2"><PackageX className="size-4 text-fg-muted" /> {t("Aufträge mit Fehlbestand")}</h2>
      <p className="text-[12.5px] text-fg-muted mt-0.5">{t("Nur für diesen Shop. Am einzelnen Auftrag lässt sich die Übertragung jederzeit erzwingen.")}</p>
      <ChoiceList
        value={toChoice(current)}
        disabled={!canManage(role)}
        inheritText={tenant.data ? `Derzeit: ${label(toChoice(tenant.data))}` : "Kunden-Einstellung"}
        onChange={(c) => save.mutate(fromChoice(c))}
        limits={current}
        onLimits={(l) => save.mutate({ ...current, ...l })}
      />
    </Card>
  );
}
